import * as React from 'react';
import { DockviewReact } from 'dockview';
import type {
  DockviewReadyEvent,
  IDockviewPanelProps,
  IDockviewPanelHeaderProps,
  DockviewApi,
  DockviewDidDropEvent,
  Position,
} from 'dockview';
import 'dockview/dist/styles/dockview.css';
import './FormaDock.css';
import { Prefs } from '../services/prefsService';
import { FirmPrefs } from '../services/firmPrefsService';

/* ─── Layout persistence ───
   Stored as { v, ts, w, h, layout } in BOTH localStorage (fast first paint)
   and user_prefs (follows the user across machines). Newest ts wins. The
   saved window size lets us re-apply panel sizes as PROPORTIONS — a 320px
   rail saved on a 2560px monitor restores as the same fraction of a 1440px
   laptop, not as 320 fixed pixels eating the screen. */
interface DockBlob {
  v: 2;
  ts: number;
  w: number;
  h: number;
  layout: any;
  /** Every palette id this layout has ever been offered. A palette missing
      from `seen` is NEW (shipped after the layout was saved) and gets added
      on restore; one that's in `seen` but not in the layout was deliberately
      closed and stays closed. This is why adding a palette never needs a
      layoutKey bump — bumping wipes everyone's arrangement. */
  seen?: string[];
}

/** Automated capture/QA runs sign in as a real user; without this they would
    overwrite that user's CLOUD layout with a throwaway one and their next
    launch would "revert" to the harness layout. Local persistence still works
    so a harness can test restore within its own profile. */
const cloudPersistDisabled = () => {
  try {
    return localStorage.getItem('sf.dockNoCloudPersist') === '1';
  } catch {
    return false;
  }
};

function readBlob(raw: string | null): DockBlob | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw);
    if (p && p.v === 2 && p.layout) return p as DockBlob;
    // Legacy shape: a bare dockview toJSON() — restorable, unknown size/age.
    if (p && p.grid) return { v: 2, ts: 0, w: 0, h: 0, layout: p };
  } catch {
    /* corrupt — ignore */
  }
  return null;
}

/** Scale every node size in a serialized dockview grid by the ratio between
    the saved window and the current one. Dockview's grid alternates
    orientation per level; a node's own `size` lies along its PARENT's axis. */
function rescaleLayout(blob: DockBlob, curW: number, curH: number): any {
  const saved = JSON.parse(JSON.stringify(blob.layout));
  const g = saved?.grid;
  if (!g?.root || !blob.w || !blob.h || !curW || !curH) return saved;
  const rw = curW / blob.w;
  const rh = curH / blob.h;
  if (Math.abs(rw - 1) < 0.01 && Math.abs(rh - 1) < 0.01) return saved;
  const scale = (node: any, parentHorizontal: boolean) => {
    if (typeof node.size === 'number') {
      node.size = Math.max(40, Math.round(node.size * (parentHorizontal ? rw : rh)));
    }
    if (node.type === 'branch' && Array.isArray(node.data)) {
      node.data.forEach((c: any) => scale(c, !parentHorizontal));
    }
  };
  const rootHorizontal = g.orientation === 'HORIZONTAL';
  // The root's own size lies along the cross axis of its orientation.
  scale(g.root, !rootHorizontal);
  g.width = curW;
  g.height = curH;
  return saved;
}

/**
 * FormaDock — the Bluebeam-style dockable workspace for the Forma designer
 * (and, later, the spec editors). Built on the dockview docking engine.
 *
 * Interaction model (Josh's spec):
 *  • LOCKED = clean work view. No close buttons, no drag, no resize. Nothing to
 *    accidentally break. This is the default.
 *  • UNLOCKED = edit the layout. A tray of every available tool palette appears at
 *    the top; drag one down onto the dock (or click it) to place it. Panels show a
 *    close (×); closing one returns it to the tray — nothing is ever lost.
 *  • The Document (canvas) can never be closed.
 *
 * Content isn't rewritten: each panel is a portal host that hands its DOM container
 * back to the parent, which React-portals the existing panel JSX into it. Every
 * current function is preserved. See memory [[project-dockable-workspace]].
 */

export interface PaletteDef {
  id: string;
  title: string;
  /** false → no close button, ever (the Document). Defaults to true. */
  closable?: boolean;
  /** true → hide the tab header entirely (the Document is the anchored core). */
  noTab?: boolean;
  /** Where it lands in the default layout / when re-added from the tray. */
  defaultPosition?: {
    direction: 'left' | 'right' | 'above' | 'below' | 'within';
    /** id of the panel to position relative to (defaults to the canvas). */
    reference?: string;
  };
  /** Preferred width in px when docked left/right. */
  width?: number;
  /** true → the palette exists and is fully usable, but is NOT part of the
      default layout (the user opens it from the menu or the tray when they
      want it). Additive: omitted, behaviour is exactly as before. */
  defaultClosed?: boolean;
}

interface DockContextValue {
  locked: boolean;
  onMount: (id: string, el: HTMLElement) => void;
  onUnmount: (id: string) => void;
  closePanel: (id: string) => void;
  isClosable: (id: string) => boolean;
}
const DockContext = React.createContext<DockContextValue | null>(null);

/** Portal host: exposes its content DOM node upward via context. */
function PanelHost(props: IDockviewPanelProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const ctx = React.useContext(DockContext);
  const id = props.api.id;
  React.useEffect(() => {
    if (ref.current && ctx) ctx.onMount(id, ref.current);
    return () => ctx?.onUnmount(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // When a palette is docked top/bottom it's wide + short — flip its contents to a
  // horizontal flow so it takes less vertical room. Detected from the box shape.
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const { width, height } = el.getBoundingClientRect();
      el.classList.toggle('forma-panel-horizontal', width >= 520 && width > height * 1.5);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      data-dock-panel={id}
      style={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    />
  );
}

/** Custom tab: title + a close (×) shown only when unlocked and the panel is closable. */
function FormaTab(props: IDockviewPanelHeaderProps) {
  const ctx = React.useContext(DockContext);
  const id = props.api.id;
  const [title, setTitle] = React.useState(props.api.title ?? id);
  React.useEffect(() => {
    const d = props.api.onDidTitleChange(() => setTitle(props.api.title ?? id));
    return () => d.dispose();
  }, [props.api, id]);
  const showClose = !!ctx && !ctx.locked && ctx.isClosable(id);
  return (
    <div className="forma-tab">
      <span className="forma-tab-title">{title}</span>
      {showClose && (
        <button
          className="forma-tab-close"
          title="Close — drag its top-bar button back in to reopen it"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            ctx.closePanel(id);
          }}
        >
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 4l8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

const COMPONENTS = { panel: PanelHost };
const TAB_COMPONENTS = { forma: FormaTab };

const POSITION_TO_DIRECTION: Record<string, 'left' | 'right' | 'above' | 'below' | 'within'> = {
  left: 'left',
  right: 'right',
  top: 'above',
  bottom: 'below',
  center: 'within',
};

export interface FormaDockHandle {
  showPanel: (id: string) => void;
  hidePanel: (id: string) => void;
  togglePanel: (id: string) => void;
  isPanelOpen: (id: string) => boolean;
  /** Back to square one: drops the user's saved layout so the ladder
   *  (firm default → out-of-box) resolves again. */
  resetLayout: () => void;
  /** The arrangement on screen right now, in the stored blob shape —
   *  what "Set as firm default" publishes (contract §2.9). */
  snapshotLayout: () => unknown;
}

export interface FormaDockProps {
  isDark: boolean;
  locked: boolean;
  layoutKey: string;
  /** Every palette that can appear in this workspace (first entry = the Document). */
  palettes: PaletteDef[];
  /** firm_prefs key holding this workspace's FIRM default layout. The
   *  resolution ladder is user → firm → out-of-box (contract §2.9): a user
   *  who has saved their own arrangement never sees the firm's. */
  firmLayoutKey?: string;
  onContainersChange: (containers: Record<string, HTMLElement>) => void;
  onReady?: (handle: FormaDockHandle) => void;
}

export function FormaDock({
  isDark,
  locked,
  layoutKey,
  palettes,
  firmLayoutKey,
  onContainersChange,
  onReady,
}: FormaDockProps) {
  const apiRef = React.useRef<DockviewApi | null>(null);
  const elsRef = React.useRef<Record<string, HTMLElement>>({});
  const palettesRef = React.useRef(palettes);
  palettesRef.current = palettes;
  const [openIds, setOpenIds] = React.useState<string[]>([]);
  // Always-current key: the old code captured layoutKey in a closure at
  // dock-ready, so when the key changed (anon → signed-in user id) every save
  // landed under the WRONG key and the next launch "reset" the workspace.
  const layoutKeyRef = React.useRef(layoutKey);
  layoutKeyRef.current = layoutKey;
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const firmKeyRef = React.useRef(firmLayoutKey);
  firmKeyRef.current = firmLayoutKey;
  // ts of the layout currently on screen — cloud copies older than this never
  // clobber what the user is looking at.
  const appliedTsRef = React.useRef(0);
  // Latest tryCloud closure — re-run on window focus so a layout tweaked on
  // the other machine lands as soon as you come back to this one.
  const tryCloudRef = React.useRef<(() => void) | null>(null);
  // Palette ids this layout has already been offered (see DockBlob.seen).
  const seenRef = React.useRef<string[]>([]);

  const defOf = (id: string) => palettesRef.current.find((p) => p.id === id);

  const emitContainers = React.useCallback(() => {
    onContainersChange({ ...elsRef.current });
  }, [onContainersChange]);

  const refreshOpen = React.useCallback(() => {
    const api = apiRef.current;
    if (api) setOpenIds(api.panels.map((p) => p.id));
  }, []);

  const isClosable = React.useCallback((id: string) => defOf(id)?.closable !== false, []);

  const ctxValue = React.useMemo<DockContextValue>(
    () => ({
      locked,
      onMount: (id, el) => {
        elsRef.current[id] = el;
        emitContainers();
      },
      onUnmount: (id) => {
        delete elsRef.current[id];
        emitContainers();
      },
      closePanel: (id) => apiRef.current?.getPanel(id)?.api.close(),
      isClosable,
    }),
    [locked, emitContainers, isClosable]
  );

  const addPalette = React.useCallback((id: string) => {
    const api = apiRef.current;
    const def = defOf(id);
    if (!api || !def || api.getPanel(id)) return;
    const pos = def.defaultPosition;
    // A reference panel the user has closed would make addPanel throw and the
    // palette would silently never appear — fall back to the canvas anchor.
    const ref = pos?.reference ?? 'canvas';
    const refOk = api.getPanel(ref) ? ref : 'canvas';
    const position = pos
      ? { referencePanel: refOk, direction: refOk === ref ? pos.direction : 'right' }
      : undefined;
    api.addPanel({
      id,
      component: 'panel',
      tabComponent: 'forma',
      title: def.title,
      position: position as any,
    });
    if (def.width) {
      try {
        api.getPanel(id)?.api.setSize({ width: def.width });
      } catch {
        /* best-effort */
      }
    }
  }, []);

  // Panels flagged noTab (the Document) are the anchored core: hide their tab
  // header and refuse drops into their group, so they always read as the canvas
  // the palettes dock around — never a closable/tabbed panel.
  const enforceAnchors = React.useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    for (const def of palettesRef.current) {
      if (!def.noTab) continue;
      const panel = api.getPanel(def.id);
      if (panel?.group) {
        try {
          panel.group.locked = 'no-drop-target';
          panel.group.header.hidden = true;
        } catch {
          /* best-effort */
        }
      }
    }
  }, []);

  const buildDefault = React.useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    try {
      api.clear();
    } catch {
      /* nothing to clear */
    }
    for (const def of palettesRef.current) {
      if (def.defaultClosed) continue;
      addPalette(def.id);
    }
    // A defaultClosed palette has still been OFFERED — record it as seen so
    // the additive-migration path below never force-opens it later.
    seenRef.current = palettesRef.current.map((p) => p.id);
    // dockview activates each panel as it's added, so every tabbed group
    // would open on its LAST tab (the fresh-default "why is Watermark
    // showing?" bug). Activate each group's FIRST tab instead.
    try {
      for (const group of api.groups) {
        group.panels[0]?.api.setActive();
      }
    } catch {
      /* best-effort */
    }
    // Re-apply the side-dock widths LAST. Each addPanel splits its reference
    // 50/50 before the per-panel setSize lands, so an early width gets undone
    // by whatever docks next — and the canvas ends up squeezed between two
    // half-screen rails. The canvas is the hero (DESIGN §3.4): it keeps every
    // pixel the palettes did not ask for.
    try {
      for (const def of palettesRef.current) {
        const dir = def.defaultPosition?.direction;
        if (!def.width || (dir !== 'left' && dir !== 'right')) continue;
        api.getPanel(def.id)?.api.setSize({ width: def.width });
      }
    } catch {
      /* best-effort */
    }
    api.getPanel('canvas')?.api.setActive();
    enforceAnchors();
  }, [addPalette, enforceAnchors]);

  const dockSize = React.useCallback((): { w: number; h: number } => {
    const r = bodyRef.current?.getBoundingClientRect();
    return { w: Math.round(r?.width || 0), h: Math.round(r?.height || 0) };
  }, []);

  /** Build the blob describing what is on screen right now. */
  const snapshot = React.useCallback((): DockBlob | null => {
    const api = apiRef.current;
    if (!api) return null;
    const { w, h } = dockSize();
    const seen = [...new Set([...seenRef.current, ...api.panels.map((p) => p.id)])];
    return { v: 2, ts: Date.now(), w, h, layout: api.toJSON(), seen };
  }, [dockSize]);

  /* Applying an INHERITED layout (the firm default) must not turn the user
     into a customised user — otherwise their first visit would silently
     snapshot the firm's arrangement as their own and they'd never receive an
     updated firm default again. So the write that the restore itself
     triggers is swallowed; the moment they actually drag something, the
     layout becomes theirs and saves normally. */
  const inheritUntilRef = React.useRef(0);

  const persist = React.useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    if (Date.now() < inheritUntilRef.current) return;
    try {
      const { w, h } = dockSize();
      const seen = [...new Set([...seenRef.current, ...api.panels.map((p) => p.id)])];
      seenRef.current = seen;
      const blob: DockBlob = { v: 2, ts: Date.now(), w, h, layout: api.toJSON(), seen };
      appliedTsRef.current = blob.ts;
      const key = layoutKeyRef.current;
      localStorage.setItem(key, JSON.stringify(blob));
      // Cloud copy — the layout follows the user to every machine.
      if (!cloudPersistDisabled()) Prefs.set(key, blob);
    } catch {
      /* private mode / quota — non-fatal */
    }
  }, [dockSize]);

  // Debounced save on ANY mouse/drag release inside the dock: sash resizes
  // don't reliably fire onDidLayoutChange, which is exactly why resized
  // panels never stuck.
  const persistSoonRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistSoon = React.useCallback(() => {
    if (persistSoonRef.current) clearTimeout(persistSoonRef.current);
    persistSoonRef.current = setTimeout(persist, 400);
  }, [persist]);

  // Window resizes re-flow the dock without any mouseup inside it — keep the
  // saved blob in step so its w/h always describe the layout it contains.
  React.useEffect(() => {
    const onResize = () => persistSoon();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (persistSoonRef.current) clearTimeout(persistSoonRef.current);
    };
  }, [persistSoon]);

  // Cross-machine follow happens on dock mount (editor entry) ONLY. It used
  // to also fire on every window focus — which tore the dock down and
  // re-portaled every palette mid-session, and with any clock skew between
  // machines made the layout "snap back" every alt-tab. Entering the editor
  // is the sync point; while you're in it, your layout is yours.

  const applyBlob = React.useCallback(
    (blob: DockBlob): boolean => {
      const api = apiRef.current;
      if (!api) return false;
      try {
        const { w, h } = dockSize();
        api.fromJSON(rescaleLayout(blob, w, h));
        appliedTsRef.current = blob.ts;
        if (api.panels.length === 0) return false;
        // Additive migration: a palette shipped AFTER this layout was saved
        // joins it instead of being invisible until someone bumps layoutKey
        // (a bump throws away every user's arrangement — never again).
        // Legacy blobs carry no `seen`, so what's in the layout counts as seen
        // and only genuinely new palettes are added.
        const restoredIds = api.panels.map((p) => p.id);
        seenRef.current = [...new Set([...(blob.seen ?? restoredIds), ...restoredIds])];
        const unseen = palettesRef.current.filter((p) => !seenRef.current.includes(p.id));
        // Everything unseen counts as offered from here on; only the ones that
        // belong in a default layout actually get placed.
        seenRef.current = [...seenRef.current, ...unseen.map((d) => d.id)];
        const missing = unseen.filter((p) => !p.defaultClosed);
        if (missing.length) {
          // dockview activates each panel as it's added, so a palette shipped
          // today would open ON TOP of whatever tab the user left active
          // ("why is Project Tokens showing?"). Put the active tabs back.
          const wasActive = api.groups.map((g) => g.activePanel?.id).filter(Boolean) as string[];
          for (const def of missing) addPalette(def.id);
          for (const id of wasActive) api.getPanel(id)?.api.setActive();
          api.getPanel('canvas')?.api.setActive();
          console.log(
            '[dock] added new palette(s) to your layout:',
            missing.map((d) => d.id).join(', ')
          );
          persistSoon();
        }
        return true;
      } catch (e) {
        console.warn('[dock] layout restore failed — rebuilding default:', e);
        return false;
      }
    },
    [dockSize, addPalette, persistSoon]
  );

  /** Rung 2 of the ladder: the layout an admin published for the firm. */
  const firmBlob = React.useCallback((): DockBlob | null => {
    const fk = firmKeyRef.current;
    if (!fk) return null;
    const raw = FirmPrefs.get(fk);
    if (!raw) return null;
    return readBlob(typeof raw === 'string' ? raw : JSON.stringify(raw));
  }, []);

  /** Apply an inherited layout without claiming it as the user's own. */
  const applyInherited = React.useCallback(
    (blob: DockBlob): boolean => {
      inheritUntilRef.current = Date.now() + 1500;
      const ok = applyBlob(blob);
      if (!ok) inheritUntilRef.current = 0;
      // An inherited layout is not "what this user last saved", so it must
      // never out-rank the user's own cloud copy on the ts comparison.
      else appliedTsRef.current = 0;
      return ok;
    },
    [applyBlob]
  );

  const handleReady = (event: DockviewReadyEvent) => {
    const api = event.api;
    apiRef.current = api;
    const key = layoutKeyRef.current;

    // The ladder (contract §2.9): the user's own layout, then the FIRM
    // default, then the app's out-of-box arrangement. Local first (instant),
    // then the cloud copy if it's newer — so the machine you tweaked last
    // always wins, everywhere.
    let restored = false;
    const local = readBlob(localStorage.getItem(key));
    if (local) restored = applyBlob(local);
    let inherited = false;
    if (!restored) {
      const firm = firmBlob();
      if (firm) inherited = restored = applyInherited(firm);
    }
    if (!restored) buildDefault();
    const hadOwnLayout = !!local;
    // The Document is load-bearing: if a stale layout dropped it, put it back.
    if (!api.getPanel('canvas')) addPalette('canvas');
    enforceAnchors();

    const tryCloud = () => {
      const cloud = Prefs.get(key) as DockBlob | undefined;
      if (cloud?.v === 2 && cloud.layout && cloud.ts > appliedTsRef.current) {
        if (applyBlob(cloud)) {
          if (!api.getPanel('canvas')) addPalette('canvas');
          enforceAnchors();
          refreshOpen();
          try {
            localStorage.setItem(key, JSON.stringify(cloud));
          } catch {
            /* non-fatal */
          }
        } else {
          // fromJSON CLEARS the dock before it throws — a corrupt cloud blob
          // must never leave the user staring at an empty editor. Rebuild.
          const local = readBlob(localStorage.getItem(key));
          if (!(local && applyBlob(local))) buildDefault();
          if (!api.getPanel('canvas')) addPalette('canvas');
          enforceAnchors();
          refreshOpen();
        }
      }
    };
    tryCloudRef.current = tryCloud;
    if (Prefs.isReady()) tryCloud();
    else {
      const un = Prefs.subscribe(() => {
        un();
        tryCloud();
      });
    }
    // Cross-machine follow: the login-time pref snapshot goes stale, so
    // re-pull from the server on every dock mount (= every editor entry).
    // A layout saved on the other machine applies here without a re-login.
    Prefs.refetch().then(tryCloud);

    // The firm default may still be in flight at mount. If this user has no
    // layout of their own anywhere, take the firm's as soon as it lands —
    // a new hire should never see out-of-box when their firm has published
    // an arrangement.
    const tryFirm = () => {
      if (hadOwnLayout || inherited) return;
      if (appliedTsRef.current > 0) return; // the user's cloud layout arrived first
      const firm = firmBlob();
      if (!firm) return;
      if (applyInherited(firm)) {
        inherited = true;
        if (!api.getPanel('canvas')) addPalette('canvas');
        enforceAnchors();
        refreshOpen();
      }
    };
    if (FirmPrefs.isReady()) tryFirm();
    else {
      const unFirm = FirmPrefs.subscribe(() => {
        unFirm();
        tryFirm();
      });
    }

    // Left/right docks are SUPERIOR (DESIGN.md §4): a top/bottom drop on the
    // dock's outer edge would span the full width and squeeze the side docks.
    // Intercept it and land the panel in the CENTER column (relative to the
    // Document) instead — full-height side docks keep their width, top/bottom
    // strips get only the width between them.
    api.onWillDrop((e) => {
      if (e.group) return; // in-column drops are fine anywhere
      if (e.position !== 'top' && e.position !== 'bottom') return;
      const data = e.getData();
      if (!data) return; // external tray drags are re-routed in handleDrop
      e.preventDefault();
      const canvasGroup = api.getPanel('canvas')?.group;
      if (!canvasGroup) return;
      const target = data.panelId ? api.getPanel(data.panelId)?.api : undefined;
      const groupTarget = !data.panelId
        ? api.groups.find((g) => g.id === data.groupId)?.api
        : undefined;
      (target ?? groupTarget)?.moveTo({ group: canvasGroup, position: e.position });
    });

    api.onDidLayoutChange(() => {
      persistSoon();
      refreshOpen();
      enforceAnchors();
    });
    api.onDidAddPanel(() => refreshOpen());
    api.onDidRemovePanel(() => refreshOpen());
    // Accept drags of external palette chips so dockview shows drop overlays —
    // EXCEPT a drop INTO the canvas (the anchored Document). A center-drop there
    // is a no-op, so lighting it up is misleading; leave it unhandled (no
    // overlay). Docking a palette to the canvas's edges still works.
    api.onUnhandledDragOverEvent((e) => {
      const overCanvas = (e as any).group?.panels?.some((p: any) => p.id === 'canvas');
      if (overCanvas && (e as any).position === 'center') return;
      e.accept();
    });
    refreshOpen();

    const handle: FormaDockHandle = {
      // Open it if it isn't placed, and ALWAYS bring it forward: asking for a
      // tool that is buried behind three tabs must surface it, not no-op.
      showPanel: (id) => {
        addPalette(id);
        try {
          api.getPanel(id)?.api.setActive();
        } catch {
          /* best-effort */
        }
      },
      hidePanel: (id) => api.getPanel(id)?.api.close(),
      togglePanel: (id) => (api.getPanel(id) ? api.getPanel(id)?.api.close() : addPalette(id)),
      isPanelOpen: (id) => !!api.getPanel(id),
      resetLayout: () => {
        try {
          localStorage.removeItem(layoutKeyRef.current);
        } catch {
          /* ignore */
        }
        // Reset means "put me back where a colleague with no preferences
        // would be" — which is the FIRM default when one exists, and only
        // then the out-of-box arrangement (contract §2.9).
        const firm = firmBlob();
        if (!(firm && applyInherited(firm))) buildDefault();
        if (!api.getPanel('canvas')) addPalette('canvas');
        enforceAnchors();
        refreshOpen();
        // buildDefault fires onDidLayoutChange → persist, which re-saves the
        // fresh default to BOTH stores (so reset propagates across machines).
      },
      snapshotLayout: () => snapshot(),
    };
    onReady?.(handle);
  };

  const handleDrop = (event: DockviewDidDropEvent) => {
    // Internal tab moves carry dockview data — let the engine handle those.
    if (event.getData()) return;
    const id = event.nativeEvent.dataTransfer?.getData('application/x-forma-palette');
    const api = apiRef.current;
    if (!id || !api || !defOf(id) || api.getPanel(id)) return;
    const def = defOf(id)!;
    const dir = POSITION_TO_DIRECTION[event.position as Position] ?? 'right';
    const group = (event as any).group;
    // Left/right docks are SUPERIOR: an outer-edge top/bottom drop lands in
    // the center column (relative to the Document), never spanning full width.
    const position = group
      ? { referenceGroup: group.id, direction: dir }
      : dir === 'above' || dir === 'below'
        ? { referencePanel: 'canvas', direction: dir }
        : { direction: dir };
    api.addPanel({
      id,
      component: 'panel',
      tabComponent: 'forma',
      title: def.title,
      position: position as any,
    });
    // Give the newly-docked palette a SENSIBLE default size — dockview otherwise
    // splits the space 50/50 (a palette eating half the screen). Side docks use
    // the palette's natural width; top/bottom docks get a strip tall enough for
    // the horizontal one-row flow.
    try {
      const panel = api.getPanel(id);
      if (dir === 'left' || dir === 'right') panel?.api.setSize({ width: def.width ?? 320 });
      else if (dir === 'above' || dir === 'below') panel?.api.setSize({ height: 150 });
    } catch {
      /* best-effort */
    }
  };

  return (
    <DockContext.Provider value={ctxValue}>
      <div className="forma-dock-wrap">
        <div
          className="forma-dock-body"
          ref={bodyRef}
          // Sash drags end in a mouseup/dragend that dockview doesn't surface
          // as a layout event — capture them here so resizes actually save.
          onMouseUpCapture={persistSoon}
          onDragEndCapture={persistSoon}
        >
          <DockviewReact
            // Remount when the layout key changes (anon → user id at sign-in):
            // the restore path re-runs against the RIGHT key instead of saving
            // one user's workspace under another key.
            key={layoutKey}
            className={`forma-dock ${isDark ? 'dockview-theme-dark' : 'dockview-theme-light'}${locked ? ' locked' : ''}`}
            onReady={handleReady}
            onDidDrop={handleDrop}
            components={COMPONENTS}
            tabComponents={TAB_COMPONENTS}
            defaultTabComponent={FormaTab}
            locked={locked}
            disableDnd={locked}
            disableFloatingGroups={false}
          />
        </div>
      </div>
    </DockContext.Provider>
  );
}
